import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  SidebarFooter,
  SidebarUserEmail,
  SidebarUserIdentity,
  SidebarUserInfo,
  SidebarUserMenu,
  SidebarUserMenuDivider,
  SidebarUserMenuIndicator,
  SidebarUserMenuItem,
  SidebarUserMenuLink,
  SidebarUserName,
  SidebarUserSkeleton,
  SidebarUserTrigger,
} from "./Sidebar.styles";
import {
  dropDownArrowIcon as DropDownArrowIcon,
  logoutIcon as LogoutIcon,
} from "assets/icons";

type SidebarMenuIcon = React.FC<React.SVGProps<SVGSVGElement>>;

export interface SidebarUserMenuLinkConfig {
  to: string;
  label: string;
  icon: SidebarMenuIcon;
}

export interface SidebarUserMenuActionConfig {
  label: string;
  icon: SidebarMenuIcon;
  onSelect: () => void;
}

interface SidebarUserFooterProps {
  name: string;
  email: string;
  isLoading?: boolean;
  menuLinks?: SidebarUserMenuLinkConfig[];
  menuActions?: SidebarUserMenuActionConfig[];
  onLogout: () => void;
  logoutTo?: string;
}

const SidebarUserFooter: React.FC<SidebarUserFooterProps> = ({
  name,
  email,
  isLoading = false,
  menuLinks = [],
  menuActions = [],
  onLogout,
  logoutTo = "/login",
}) => {
  const navigate = useNavigate();
  const footerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  useEffect(() => {
    if (!userMenuOpen) return undefined;

    menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();

    const handlePointerDown = (event: MouseEvent) => {
      if (!footerRef.current?.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setUserMenuOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [userMenuOpen]);

  const handleUserMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
    );
    if (!items.length) return;

    const currentIndex = items.indexOf(document.activeElement as HTMLElement);
    const focusAt = (index: number) => {
      event.preventDefault();
      items[index]?.focus();
    };

    if (event.key === "ArrowDown") {
      focusAt(currentIndex < 0 ? 0 : (currentIndex + 1) % items.length);
      return;
    }
    if (event.key === "ArrowUp") {
      focusAt(currentIndex <= 0 ? items.length - 1 : currentIndex - 1);
      return;
    }
    if (event.key === "Home") {
      focusAt(0);
      return;
    }
    if (event.key === "End") {
      focusAt(items.length - 1);
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      setUserMenuOpen(false);
      triggerRef.current?.focus();
      return;
    }
    if (event.key === "Tab") {
      setUserMenuOpen(false);
    }
  };

  const handleLogout = () => {
    setUserMenuOpen(false);
    onLogout();
    navigate(logoutTo);
  };

  const hasCustomMenuItems = menuLinks.length > 0 || menuActions.length > 0;

  return (
    <SidebarFooter ref={footerRef}>
      <SidebarUserTrigger
        ref={triggerRef}
        type="button"
        aria-label="Профиль пользователя"
        aria-haspopup="menu"
        aria-expanded={userMenuOpen}
        onClick={() => setUserMenuOpen((current) => !current)}
      >
        <SidebarUserInfo>
          <SidebarUserIdentity>
            {isLoading ? (
              <>
                <SidebarUserSkeleton
                  data-testid="sidebar-user-name-skeleton"
                  $width={120}
                  $height={13}
                  aria-hidden="true"
                />
                <SidebarUserSkeleton
                  data-testid="sidebar-user-email-skeleton"
                  $width={150}
                  $height={11}
                  aria-hidden="true"
                />
              </>
            ) : (
              <>
                <SidebarUserName title={name}>{name}</SidebarUserName>
                <SidebarUserEmail title={email}>{email}</SidebarUserEmail>
              </>
            )}
          </SidebarUserIdentity>
          <SidebarUserMenuIndicator
            as={DropDownArrowIcon}
            $open={userMenuOpen}
            aria-hidden="true"
          />
        </SidebarUserInfo>
      </SidebarUserTrigger>

      {userMenuOpen && (
        <SidebarUserMenu
          ref={menuRef}
          role="menu"
          onKeyDown={handleUserMenuKeyDown}
        >
          {menuLinks.map(({ to, label, icon: Icon }) => (
            <SidebarUserMenuLink
              key={to}
              to={to}
              role="menuitem"
              onClick={() => setUserMenuOpen(false)}
            >
              <Icon aria-hidden="true" />
              {label}
            </SidebarUserMenuLink>
          ))}
          {menuActions.map(({ label, icon: Icon, onSelect }) => (
            <SidebarUserMenuItem
              key={label}
              type="button"
              role="menuitem"
              onClick={() => {
                setUserMenuOpen(false);
                onSelect();
              }}
            >
              <Icon aria-hidden="true" />
              {label}
            </SidebarUserMenuItem>
          ))}
          {hasCustomMenuItems && <SidebarUserMenuDivider role="separator" />}
          <SidebarUserMenuItem
            type="button"
            role="menuitem"
            $danger
            onClick={handleLogout}
          >
            <LogoutIcon aria-hidden="true" />
            Выйти
          </SidebarUserMenuItem>
        </SidebarUserMenu>
      )}
    </SidebarFooter>
  );
};

export default SidebarUserFooter;
